import { useEffect, useState, useMemo } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import Breadcrumb from "@/components/feature/Breadcrumb";
import VideoCard from "@/components/base/VideoCard";
import { categories, categoryMap } from "@/mocks/categories";
import { videos } from "@/mocks/videos";

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const [searchParams] = useSearchParams();
  const searchQuery = searchParams.get("q") || "";

  const [activeSort, setActiveSort] = useState("hot");
  const [activeQuality, setActiveQuality] = useState("all");

  const currentCategory = slug && slug !== "all" ? categoryMap[slug] : null;

  useEffect(() => {
    if (currentCategory) {
      document.title = `${currentCategory.name} - 妖精视频 | 高清免费在线观看`;
    } else if (slug === "all") {
      document.title = "全部视频 - 妖精视频 | 高清免费在线观看";
    }
  }, [currentCategory, slug]);

  const filteredVideos = useMemo(() => {
    let result = videos;

    if (slug && slug !== "all") {
      result = result.filter((v) => v.category === slug);
    }

    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      result = result.filter(
        (v) =>
          v.title.toLowerCase().includes(q) ||
          v.tags.some((t) => t.toLowerCase().includes(q))
      );
    }

    if (activeQuality !== "all") {
      result = result.filter((v) => v.quality === activeQuality);
    }

    if (activeSort === "hot") {
      result = [...result].sort((a, b) => b.rating - a.rating);
    } else if (activeSort === "new") {
      result = [...result].sort((a, b) => (b.isNew ? 1 : 0) - (a.isNew ? 1 : 0));
    } else if (activeSort === "views") {
      result = [...result].sort((a, b) => {
        const parseViews = (v: string) => parseInt(v.replace("万", "0000"));
        return parseViews(b.views) - parseViews(a.views);
      });
    }

    return result;
  }, [slug, searchQuery, activeQuality, activeSort]);

  const pageTitle = currentCategory
    ? currentCategory.name
    : slug === "all"
    ? "全部视频"
    : "分类";

  const pageDescription = currentCategory
    ? currentCategory.description
    : "妖精视频水果植物系列，全部平台高清视频在线观看，电视剧全集不缓冲。";

  const breadcrumbItems = [
    { label: "首页", href: "/" },
    { label: pageTitle },
  ];

  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "CollectionPage",
    name: `${pageTitle} - 妖精视频`,
    description: pageDescription,
    url: `https://www.yaojingshipin.com/category/${slug || "all"}`,
    isPartOf: { "@id": "https://www.yaojingshipin.com/#website" },
  };

  return (
    <div className="min-h-screen bg-background-50">
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }}
      />

      <Navbar />

      <main className="pt-16">
        <Breadcrumb items={breadcrumbItems} />

        <section className="px-4 md:px-6 py-8 md:py-12">
          <div className="max-w-7xl mx-auto">
            <div className="mb-8">
              <h1 className="text-2xl md:text-3xl font-heading text-foreground-950 mb-2">
                {pageTitle}
              </h1>
              {pageDescription && (
                <p className="text-sm text-foreground-600 max-w-2xl">
                  {pageDescription}
                </p>
              )}
            </div>

            <div className="flex items-center gap-2 mb-3 flex-wrap">
              {categories.map((cat) => (
                <button
                  key={cat.id}
                  onClick={() => {
                    window.REACT_APP_NAVIGATE?.(`/category/${cat.slug}`);
                    window.scrollTo({ top: 0, behavior: "instant" });
                  }}
                  className={`px-4 py-2 rounded-full text-sm font-medium transition-all whitespace-nowrap cursor-pointer ${
                    slug === cat.slug
                      ? "bg-accent-500 text-background-50"
                      : "bg-background-100 text-foreground-600 hover:bg-background-200 border border-background-200/70"
                  }`}
                >
                  {cat.name}
                </button>
              ))}
              <button
                onClick={() => {
                  window.REACT_APP_NAVIGATE?.("/category/all");
                  window.scrollTo({ top: 0, behavior: "instant" });
                }}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-all whitespace-nowrap cursor-pointer ${
                  slug === "all"
                    ? "bg-accent-500 text-background-50"
                    : "bg-background-100 text-foreground-600 hover:bg-background-200 border border-background-200/70"
                }`}
              >
                全部
              </button>
            </div>

            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-6">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-xs text-foreground-500 whitespace-nowrap">排序：</span>
                {[
                  { key: "hot", label: "最热" },
                  { key: "new", label: "最新" },
                  { key: "views", label: "最多播放" },
                ].map((sort) => (
                  <button
                    key={sort.key}
                    onClick={() => setActiveSort(sort.key)}
                    className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all whitespace-nowrap cursor-pointer ${
                      activeSort === sort.key
                        ? "bg-secondary-500 text-background-50"
                        : "bg-background-100 text-foreground-600 hover:bg-background-200"
                    }`}
                  >
                    {sort.label}
                  </button>
                ))}
              </div>

              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-xs text-foreground-500 whitespace-nowrap">画质：</span>
                {[
                  { key: "all", label: "全部" },
                  { key: "4K", label: "4K" },
                  { key: "1080P", label: "1080P" },
                ].map((q) => (
                  <button
                    key={q.key}
                    onClick={() => setActiveQuality(q.key)}
                    className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all whitespace-nowrap cursor-pointer ${
                      activeQuality === q.key
                        ? "bg-secondary-500 text-background-50"
                        : "bg-background-100 text-foreground-600 hover:bg-background-200"
                    }`}
                  >
                    {q.label}
                  </button>
                ))}
              </div>
            </div>

            {filteredVideos.length > 0 ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
                {filteredVideos.map((video) => (
                  <VideoCard key={video.id} {...video} />
                ))}
              </div>
            ) : (
              <div className="text-center py-20">
                <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-background-100 flex items-center justify-center">
                  <i className="ri-search-line text-2xl text-foreground-400"></i>
                </div>
                <p className="text-foreground-600">没有找到相关视频</p>
                <p className="text-sm text-foreground-500 mt-1">试试其他关键词或分类</p>
              </div>
            )}

            {filteredVideos.length > 0 && (
              <p className="text-xs text-foreground-500 mt-6 text-center">
                共找到 {filteredVideos.length} 部视频
              </p>
            )}
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}